import React, { useState, useMemo, Fragment, useEffect } from "react";
import { Autocomplete, TextField, Chip, Checkbox } from "@mui/material";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import { autoTable } from "jspdf-autotable"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDown, faArrowUp, faMinus, faPlus, faSort } from "@fortawesome/free-solid-svg-icons";

// Formatting functions
const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB"); // dd/mm/yyyy
};

const formatNumber = (num) => {
    if (num == null) return "";
    return Number(num).toLocaleString("en-IN", { minimumFractionDigits: 2 });
};

const formatValue = (value, type) => {
    if (type === "date") return formatDate(value);
    if (type === "number") return formatNumber(value);
    return value;
};

const MasterGrid = ({
    columns = [],
    data = [],
    detailColumns = [],
    groupKey = {},
    showPaging = false,
    pageSize = 10,
    exportHeader = {},
    headClass = {},
    filterClass = {},
    expandableRow = false,
    showDiffBal = false
}) => {
    const [filters, setFilters] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
    const [expandedRows, setExpandedRows] = useState({});
    const [expandAllChecked, setExpandAllChecked] = useState(false);

    // Filtered data
    const filteredData = useMemo(() => {
        return data.filter((row) =>
            Object.keys(filters).every((key) => {
                const selectedValues = filters[key];
                if (!selectedValues || selectedValues.length === 0) return true;
                return selectedValues.includes(
                    formatValue(row[key], columns.find((c) => c.accessor === key)?.type)
                );
            })
        );
    }, [data, filters, columns]);

    // Sorted data
    const sortedData = useMemo(() => {
        if (!sortConfig.key || !sortConfig.direction) return filteredData; // default order

        const colType = columns.find((c) => c.accessor === sortConfig.key)?.type;

        return [...filteredData].sort((a, b) => {
            let aVal = a[sortConfig.key];
            let bVal = b[sortConfig.key];

            if (colType === "number") {
                aVal = Number(aVal) || 0;
                bVal = Number(bVal) || 0;
            } else if (colType === "date") {
                aVal = new Date(aVal).getTime() || 0;
                bVal = new Date(bVal).getTime() || 0;
            } else {
                aVal = aVal?.toString().toLowerCase() || "";
                bVal = bVal?.toString().toLowerCase() || "";
            }

            if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
            return 0;
        });
    }, [filteredData, sortConfig, columns]);

    // Pagination
    const totalPages = showPaging ? Math.ceil(sortedData.length / pageSize) : 1;

    const paginatedData = useMemo(() => {
        if (!showPaging) return sortedData;
        const start = (currentPage - 1) * pageSize;
        return sortedData.slice(start, start + pageSize);
    }, [sortedData, currentPage, pageSize, showPaging]);

    // Totals

    const totals = useMemo(() => {
        const result = {};
        columns.forEach((col) => {
            if (col.type === "number") {
                result[col.accessor] = filteredData.reduce(
                    (sum, row) => sum + (Number(row[col.accessor]) || 0),
                    0
                );
            }
        });
        return result;
    }, [filteredData, columns]);

    const diff_Balance = useMemo(() => {
        return (totals?.OpBal_Dr || 0) - (totals?.OpBal_Cr || 0)
    }, [totals])
    const handleFilterChange = (key, newValues) => {
        setFilters((prev) => ({ ...prev, [key]: newValues || [] }));
        setCurrentPage(1);
    };

    const toggleRow = (index) => {
        setExpandedRows((prev) => ({
            ...prev,
            [index]: !prev[index],
        }));
    };


    const handleSort = (col) => {
        if (!col.sortable) return;

        setSortConfig((prev) => {
            if (prev.key !== col.accessor) {
                // New column: start with ascending
                return { key: col.accessor, direction: "asc" };
            }
            // Cycle asc → desc → default
            if (prev.direction === "asc") return { key: col.accessor, direction: "desc" };
            if (prev.direction === "desc") return { key: null, direction: null }; // default
            return { key: col.accessor, direction: "asc" };
        });
    };


    // Returns an array of group objects
    const groupedData = useMemo(() => {
        if (!expandableRow || !groupKey) return [];

        const groups = {};

        filteredData.forEach((row) => {
            const key = row[groupKey] || "Other";

            if (!groups[key]) {
                groups[key] = {
                    groupName: key,
                    rows: [],
                    totals: {}
                };
            }

            groups[key].rows.push(row);
        });

        // Calculate totals for GROUP COLUMNS only
        Object.values(groups).forEach((group) => {
            columns.forEach((col) => {
                if (col.type === "number") {
                    group.totals[col.accessor] = group.rows.reduce(
                        (sum, r) => sum + (Number(r[col.accessor]) || 0),
                        0
                    );
                }
            });
        });

        return Object.values(groups);
    }, [filteredData, columns, groupKey, expandableRow]);
    // --- Export Excel & PDF (same as your previous code) ---

    const funExcelExportWithExpand = async () => {

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Report");

        const companyName = exportHeader?.companyName || "";
        const reportName = exportHeader?.reportName || "";
        const reportDate = new Date().toLocaleDateString("en-GB");

        let currentRow = 1;

        const totalColumns = Math.max(columns.length, detailColumns.length) + 1; // +1 for Serial
        const lastColumnLetter = worksheet.getColumn(totalColumns).letter;

        // ================= COMPANY NAME =================
        worksheet.mergeCells(`A${currentRow}:${lastColumnLetter}${currentRow}`);
        const companyCell = worksheet.getCell(`A${currentRow}`);
        companyCell.value = companyName;
        companyCell.font = { bold: true, size: 16 };
        companyCell.alignment = { horizontal: "center" };
        currentRow++;

        // ================= REPORT NAME =================
        worksheet.mergeCells(`A${currentRow}:${lastColumnLetter}${currentRow}`);
        const reportCell = worksheet.getCell(`A${currentRow}`);
        reportCell.value = reportName;
        reportCell.font = { bold: true, size: 13 };
        reportCell.alignment = { horizontal: "center" };
        currentRow++;

        // ================= DATE =================
        worksheet.mergeCells(`A${currentRow}:${lastColumnLetter}${currentRow}`);
        const dateCell = worksheet.getCell(`A${currentRow}`);
        dateCell.value = `Date: ${reportDate}`;
        dateCell.font = { italic: true };
        dateCell.alignment = { horizontal: "center" };
        currentRow += 2;

        // ================= HEADER =================
        const headerRow = worksheet.addRow([
            "No.",
            ...columns.map(col => col.header)
        ]);

        headerRow.font = { bold: true };

        headerRow.eachCell(cell => {
            cell.alignment = { horizontal: "center" };
            cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFDCE6F1" }
            };
            cell.border = {
                top: { style: "thin" },
                left: { style: "thin" },
                bottom: { style: "thin" },
                right: { style: "thin" }
            };
        });

        // ================= GROUP LOOP =================
        groupedData.forEach((group, gIndex) => {

            const isExpanded = expandedRows[gIndex];

            // -------- GROUP ROW --------
            const groupRow = worksheet.addRow([
                gIndex + 1,
                ...columns.map(col => {
                    if (col.accessor === groupKey) return group.groupName;
                    if (col.type === "number") return group.totals[col.accessor] || 0;
                    return "";
                })
            ]);

            groupRow.eachCell((cell, colNumber) => {
                cell.font = { bold: true };
                cell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "FFF2F2F2" }
                };

                if (columns[colNumber - 2]?.type === "number") {
                    cell.numFmt = "#,##0.00";
                    cell.alignment = { horizontal: "right" };
                }

                cell.border = {
                    top: { style: "thin" },
                    left: { style: "thin" },
                    bottom: { style: "thin" },
                    right: { style: "thin" }
                };
            });

            // -------- DETAIL SECTION --------
            if (isExpanded && group.rows.length > 0) {

                // Detail Header
                const detailHeader = worksheet.addRow([
                    "#",
                    ...detailColumns.map(col => col.header)
                ]);

                detailHeader.eachCell(cell => {
                    cell.font = { bold: true };
                    cell.fill = {
                        type: "pattern",
                        pattern: "solid",
                        fgColor: { argb: "FFD9EAD3" }
                    };
                    cell.alignment = { horizontal: "center" };
                    cell.border = {
                        top: { style: "thin" },
                        left: { style: "thin" },
                        bottom: { style: "thin" },
                        right: { style: "thin" }
                    };
                });

                // Detail Rows
                group.rows.forEach((detail, dIndex) => {

                    const detailRow = worksheet.addRow([
                        `${gIndex + 1}.${dIndex + 1}`,
                        ...detailColumns.map(col => detail[col.accessor])
                    ]);

                    detailRow.eachCell((cell, colNumber) => {

                        if (detailColumns[colNumber - 2]?.type === "number") {
                            cell.numFmt = "#,##0.00";
                            cell.alignment = { horizontal: "right" };
                        }

                        cell.border = {
                            top: { style: "thin" },
                            left: { style: "thin" },
                            bottom: { style: "thin" },
                            right: { style: "thin" }
                        };
                    });
                });

                // ===== DETAIL TOTAL ROW =====
                const detailTotalRow = worksheet.addRow([
                    "",
                    ...detailColumns.map((col, idx) => {
                        if (col.type === "number") {
                            return group.rows.reduce(
                                (sum, r) => sum + (Number(r[col.accessor]) || 0),
                                0
                            );
                        }
                        if (idx === 0) return "TOTAL";
                        return "";
                    })
                ]);

                detailTotalRow.eachCell((cell, colNumber) => {

                    cell.font = { bold: true };
                    cell.fill = {
                        type: "pattern",
                        pattern: "solid",
                        fgColor: { argb: "FFE2EFDA" }
                    };

                    if (detailColumns[colNumber - 2]?.type === "number") {
                        cell.numFmt = "#,##0.00";
                        cell.alignment = { horizontal: "right" };
                    }

                    cell.border = {
                        top: { style: "thin" },
                        left: { style: "thin" },
                        bottom: { style: "thin" },
                        right: { style: "thin" }
                    };
                });

                worksheet.addRow([]);
            }
        });

        // ================= GRAND TOTAL =================
        const grandTotalRow = worksheet.addRow([
            "",
            ...columns.map((col, idx) =>
                col.type === "number"
                    ? totals[col.accessor] || 0
                    : idx === 0 ? "GRAND TOTAL" : ""
            )
        ]);

        grandTotalRow.eachCell((cell, colNumber) => {
            cell.font = { bold: true };
            cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFD9D9D9" }
            };

            if (columns[colNumber - 2]?.type === "number") {
                cell.numFmt = "#,##0.00";
                cell.alignment = { horizontal: "right" };
            }

            cell.border = {
                top: { style: "thin" },
                left: { style: "thin" },
                bottom: { style: "thin" },
                right: { style: "thin" }
            };
        });

        // ================= DIFFERENCE ROW =================
        if (showDiffBal && diff_Balance !== 0) {

            const diffRow = worksheet.addRow([
                "",
                ...columns.map((col, idx) => {
                    if (
                        (diff_Balance < 0 && col.accessor === "OpBal_Cr") ||
                        (diff_Balance > 0 && col.accessor === "OpBal_Dr")
                    ) {
                        return Math.abs(diff_Balance);
                    }
                    if (idx === 0) return "Difference In Opening Balance";
                    return "";
                })
            ]);

            diffRow.eachCell((cell, colNumber) => {

                cell.font = { bold: true, color: { argb: "FFFF0000" } };
                cell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "FFFFE5E5" }
                };

                if (columns[colNumber - 2]?.type === "number") {
                    cell.numFmt = "#,##0.00";
                    cell.alignment = { horizontal: "right" };
                }

                cell.border = {
                    top: { style: "thin" },
                    left: { style: "thin" },
                    bottom: { style: "thin" },
                    right: { style: "thin" }
                };
            });
        }

        // ================= AUTO WIDTH =================
        worksheet.columns.forEach(column => {
            let maxLength = 8;
            column.eachCell({ includeEmpty: true }, cell => {
                const length = cell.value ? cell.value.toString().length : 0;
                if (length > maxLength) maxLength = length;
            });
            column.width = Math.min(maxLength + 2, 25);
        });

        // Freeze header
        worksheet.views = [{ state: "frozen", ySplit: 4 }];

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `${reportName || "Report"}.xlsx`);
    };
    const funExcelExport = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Report");

        const companyName = exportHeader?.companyName || "";
        const reportName = exportHeader?.reportName || "";
        const reportDate = new Date().toLocaleDateString("en-GB");

        const totalColumns = columns.length;

        // 🟢 Company Name Row
        worksheet.mergeCells(1, 1, 1, totalColumns);
        const companyRow = worksheet.getCell("A1");
        companyRow.value = companyName;
        companyRow.font = { size: 16, bold: true };
        companyRow.alignment = { horizontal: "center" };

        // 🟢 Report Name Row
        worksheet.mergeCells(2, 1, 2, totalColumns);
        const reportRow = worksheet.getCell("A2");
        reportRow.value = reportName;
        reportRow.font = { size: 13, bold: true };
        reportRow.alignment = { horizontal: "center" };

        // 🟢 Date Row
        worksheet.mergeCells(3, 1, 3, totalColumns);
        const dateRow = worksheet.getCell("A3");
        dateRow.value = `Date: ${reportDate}`;
        dateRow.font = { italic: true };
        dateRow.alignment = { horizontal: "center" };

        // Empty row
        worksheet.addRow([]);

        // 🔵 Table Header Row
        const headerRow = worksheet.addRow(columns.map(col => col.header));
        headerRow.font = { bold: true };
        headerRow.alignment = { horizontal: "center" };

        headerRow.eachCell(cell => {
            cell.border = {
                top: { style: "thin" },
                left: { style: "thin" },
                bottom: { style: "thin" },
                right: { style: "thin" },
            };
        });

        // 🔵 Data Rows
        filteredData.forEach(row => {
            const rowData = columns.map(col => {
                if (col.type === "number") {
                    return Number(row[col.accessor]) || 0;
                }
                return formatValue(row[col.accessor], col.type);
            });

            const dataRow = worksheet.addRow(rowData);

            dataRow.eachCell(cell => {
                cell.border = {
                    top: { style: "thin" },
                    left: { style: "thin" },
                    bottom: { style: "thin" },
                    right: { style: "thin" },
                };
            });
        });

        // 🔴 Totals Row
        const totalRow = columns.map((col, index) => {
            if (col.type === "number") {
                return totals[col.accessor];
            }
            if (index === 0) return "TOTAL";
            return "";
        });

        const addedTotalRow = worksheet.addRow(totalRow);
        addedTotalRow.font = { bold: true };

        addedTotalRow.eachCell(cell => {
            cell.border = {
                top: { style: "thin" },
                left: { style: "thin" },
                bottom: { style: "thin" },
                right: { style: "thin" },
            };
        });

        // 📏 Auto Column Width
        worksheet.columns.forEach(column => {
            column.width = 18;
        });

        // 💾 Save File
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `${reportName}.xlsx`);
    };

    const exportToExcel = () => {
        if (expandableRow)
            funExcelExportWithExpand()
        else
            funExcelExport()
    }

    const funPDFExport = () => {
        const doc = new jsPDF("landscape");

        const companyName = exportHeader?.companyName || "";
        const reportName = exportHeader?.reportName || "";
        const reportDate = new Date().toLocaleDateString("en-GB");

        const headers = columns.map(col => col.header);

        const body = filteredData.map(row =>
            columns.map(col =>
                formatValue(row[col.accessor], col.type)
            )
        );

        // Add totals row
        const totalRow = columns.map((col, index) => {
            if (col.type === "number") {
                return formatNumber(totals[col.accessor]);
            }
            if (index === 0) return "TOTAL";
            return "";
        });

        body.push(totalRow);

        autoTable(doc, {
            head: [headers],
            body: body,
            startY: 27, // Leave space for header
            theme: "grid",
            styles: {
                fontSize: 8,
                cellPadding: 2,
            },
            headStyles: {
                fillColor: [230, 230, 230],
                textColor: 0,
                fontStyle: "bold",
            },
            didDrawPage: function (data) {
                // 🔵 Company Name
                data.settings.margin.top = 27;
                const pageWidth = doc.internal.pageSize.getWidth();
                const pageHeight = doc.internal.pageSize.getHeight();

                doc.setFontSize(16);
                doc.setFont(undefined, "bold");
                doc.text(companyName, doc.internal.pageSize.getWidth() / 2, 15, {
                    align: "center",
                });

                // 🔵 Report Name
                doc.setFontSize(12);
                doc.setFont(undefined, "bold");
                doc.text(reportName, doc.internal.pageSize.getWidth() / 2, 23, {
                    align: "center",
                });

                // 🔵 Date
                //doc.setFontSize(10);
                // doc.setFont(undefined, "normal");
                // doc.text(`Date: ${reportDate}`, doc.internal.pageSize.getWidth() / 2, 30, {
                //     align: "center",
                // });
                doc.setFontSize(9);
                doc.text(
                    `Generated on: ${reportDate}`,
                    14,
                    pageHeight - 10
                );
                // 🔴 Page Number
                const pageNumber = doc.internal.getCurrentPageInfo().pageNumber;
                const totalPages = doc.internal.getNumberOfPages();

                doc.setFontSize(9);
                doc.text(
                    `Page ${pageNumber} of ${totalPages}`,
                    pageWidth - 20,
                    pageHeight - 10
                );
            },
        });

        doc.save(`${reportName}.pdf`);
    };
    const funPDFExportWithExpand = async () => {
        const doc = new jsPDF("l", "pt", "a4");

        const companyName = exportHeader?.companyName || "";
        const reportName = exportHeader?.reportName || "";
        const reportDate = new Date().toLocaleDateString("en-GB");

        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 40;
        const headerHeight = 65;

        let startY = headerHeight;

        // ================= HEADER =================
        const addHeader = () => {
            doc.setFontSize(16);
            doc.setFont(undefined, "bold");
            doc.text(companyName, pageWidth / 2, 35, { align: "center" });

            doc.setFontSize(12);
            doc.setFont(undefined, "normal");
            doc.text(reportName, pageWidth / 2, 55, { align: "center" });
        };

        // ================= FOOTER =================
        const addFooter = (data) => {
            const str = `Page ${data.pageNumber} of ${data.pageCount} | Generated: ${reportDate}`;
            doc.setFontSize(9);
            doc.setTextColor(120);
            doc.text(str, pageWidth - margin, pageHeight - 10, { align: "right" });
        };

        // ================= COLUMN WIDTH =================
        const computeColumnWidths = (cols) => {
            const availableWidth = pageWidth - margin * 2;
            const width = availableWidth / (cols.length + 1); // +1 for serial
            const styles = {};
            styles[0] = { cellWidth: 40, halign: "center" }; // Serial column

            cols.forEach((col, idx) => {
                styles[idx + 1] = {
                    cellWidth: width,
                    halign: col.type === "number" ? "right" : "left"
                };
            });

            return styles;
        };

        const mainColStyles = computeColumnWidths(columns);
        const detailColStyles = computeColumnWidths(detailColumns);

        // ================= MAIN LOOP =================
        groupedData.forEach((group, gIndex) => {

            const isExpanded = expandedRows[gIndex];

            // -------- GROUP ROW --------
            const groupRow = [
                gIndex + 1,
                ...columns.map(col => {
                    if (col.accessor === groupKey) return group.groupName;
                    if (col.type === "number") return formatNumber(group.totals[col.accessor]);
                    return "";
                })
            ];

            autoTable(doc, {
                startY,
                head: gIndex === 0 ? [["No.", ...columns.map(c => c.header)]] : [],
                body: [groupRow],
                theme: "grid",
                headStyles: {
                    fillColor: [41, 128, 185],
                    textColor: [255, 255, 255],
                    fontStyle: "bold",
                    halign: "center"
                },
                bodyStyles: {
                    fontSize: 8,
                    fontStyle: "bold",
                    fillColor: [242, 242, 242]
                },
                columnStyles: mainColStyles,
                margin: { left: margin, right: margin },
                didDrawPage: (data) => {
                    addHeader();
                    addFooter(data);
                    data.settings.margin.top = headerHeight;
                },
                showHead: gIndex === 0 ? "everyPage" : "never"
            });

            startY = doc.lastAutoTable.finalY + 2;

            // -------- DETAIL TABLE --------
            if (isExpanded && group.rows.length > 0) {

                const detailBody = group.rows.map((row, dIndex) => [
                    `${gIndex + 1}.${dIndex + 1}`,
                    ...detailColumns.map(col =>
                        col.type === "number"
                            ? formatNumber(row[col.accessor])
                            : row[col.accessor]
                    )
                ]);

                autoTable(doc, {
                    startY,
                    head: [["#", ...detailColumns.map(c => c.header)]],
                    body: detailBody,
                    theme: "grid",
                    headStyles: {
                        fillColor: [217, 234, 211],
                        textColor: 0,
                        fontStyle: "bold",
                        halign: "center"
                    },
                    bodyStyles: { fontSize: 8 },
                    columnStyles: detailColStyles,
                    margin: { left: margin, right: margin },
                    didDrawPage: (data) => {
                        addHeader();
                        addFooter(data);
                        data.settings.margin.top = headerHeight;
                    }
                });

                startY = doc.lastAutoTable.finalY + 2;

                // -------- DETAIL TOTAL --------
                const detailTotal = [
                    "",
                    ...detailColumns.map((col, idx) =>
                        col.type === "number"
                            ? formatNumber(
                                group.rows.reduce((sum, r) => sum + (Number(r[col.accessor]) || 0), 0)
                            )
                            : idx === 0 ? "TOTAL" : ""
                    )
                ];

                autoTable(doc, {
                    startY,
                    body: [detailTotal],
                    theme: "grid",
                    bodyStyles: {
                        fontSize: 8,
                        fontStyle: "bold",
                        fillColor: [226, 239, 218]
                    },
                    columnStyles: detailColStyles,
                    margin: { left: margin, right: margin },
                    didDrawPage: (data) => {
                        addHeader();
                        addFooter(data);
                    }
                });

                startY = doc.lastAutoTable.finalY + 5;
            }
        });

        // ================= GRAND TOTAL =================
        const grandTotalRow = [
            "",
            ...columns.map((col, idx) =>
                col.type === "number"
                    ? formatNumber(totals[col.accessor])
                    : idx === 0 ? "GRAND TOTAL" : ""
            )
        ];

        autoTable(doc, {
            startY,
            body: [grandTotalRow],
            theme: "grid",
            bodyStyles: {
                fontSize: 9,
                fontStyle: "bold",
                fillColor: [41, 128, 185],// [207, 204, 204]
                textColor: [255, 255, 255]
            },
            columnStyles: mainColStyles,
            margin: { left: margin, right: margin },
            didDrawPage: (data) => {
                addHeader();
                addFooter(data);
            }
        });

        startY = doc.lastAutoTable.finalY + 5;

        // ================= DIFFERENCE ROW =================
        if (showDiffBal && diff_Balance !== 0) {
            const diffRow = [
                "",
                ...columns.map((col, idx) => {
                    if (
                        (diff_Balance < 0 && col.accessor === "OpBal_Cr") ||
                        (diff_Balance > 0 && col.accessor === "OpBal_Dr")
                    ) {
                        return formatNumber(Math.abs(diff_Balance));
                    }
                    if (idx === 0) return "Difference In Opening Balance";
                    return "";
                })
            ];

            autoTable(doc, {
                startY,
                body: [diffRow],
                theme: "grid",
                bodyStyles: {
                    fontSize: 9,
                    fontStyle: "bold",
                    fillColor: [255, 229, 229],
                    textColor: [255, 0, 0]
                },
                columnStyles: mainColStyles,
                margin: { left: margin, right: margin },
                didDrawPage: (data) => {
                    addHeader();
                    addFooter(data);
                }
            });
        }

        doc.save(`${reportName || "Report"}.pdf`);
    };

    const exportToPDF = () => {
        if (expandableRow)
            funPDFExportWithExpand()
        else
            funPDFExport();
    }

    // useEffect(() => {
    //     if (expandableRow) {

    //         const allExpanded =  Object.values(expandedRows).every(v => v)
    //         console.log(expandedRows,allExpanded)
    //         setExpandAllChecked(allExpanded);
    //     }
    // }, [expandedRows]);

    const expandAll = (expand) => {

        const newExpanded = {};
        groupedData.forEach((group, index) => {
            newExpanded[index] = expand;
        });
        setExpandedRows(newExpanded);
    };

    return (
        <div>
            {/* Filter Chips */}
            <div className="flex flex-wrap gap-2 mb-2">
                {Object.entries(filters).map(([key, values]) =>
                    values?.map((value, index) => {
                        const column = columns.find((c) => c.accessor === key);
                        return (
                            <Chip
                                key={`${key}-${index}`}
                                label={`${column?.header}: ${value}`}
                                onDelete={() => handleFilterChange(key, values.filter((v) => v !== value))}
                                color="primary"
                                size="small"
                                sx={{ height: 22, fontSize: "11px" }}
                            />
                        );
                    })
                )}
            </div>

            {/* Export Buttons */}
            <div className="flex justify-between items-center">
                <h2 className="text-slate-800  uppercase text-xs  font-semibold text-sm">Total Records : {filteredData?.length || 0}</h2>
                {filteredData && filteredData.length > 0 ?
                    <div className="flex gap-2 mb-3">
                        <button
                            onClick={exportToExcel}
                            className="px-3 py-1 bg-green-600 text-white rounded text-sm"
                        >
                            Export Excel
                        </button>
                        <button
                            onClick={exportToPDF}
                            className="px-3 py-1 bg-red-600 text-white rounded text-sm"
                        >
                            Export PDF
                        </button>
                    </div> : ""}
            </div>
            {/* Table */}
            <div className="overflow-x-auto max-h-[70vh] border border-gray-300">
                <table className="w-full table-fixed text-xs border-collapse">
                    <thead className="sticky top-0 z-20 bg-slate-200">
                        <tr>
                            <th className={`sticky left-0 bg-slate-200  text-center font-semibold z-10 p-1 border ${headClass}`}
                                style={{ width: "50px" }}>No.</th>
                            {columns.map((col) => (
                                <th
                                    key={col.accessor}
                                    className={`border p-2 text-center cursor-pointer ${headClass}`}
                                    onClick={() => handleSort(col)}
                                    style={{ width: col.width || "100px" }}
                                >
                                    <div className="flex items-center  justify-center">
                                        {col.header}
                                        {/* Sort icon at the end */}
                                        {col.sortable && (
                                            <div className=" ml-2 flex justify-end ">
                                                {sortConfig.key === col.accessor ? (
                                                    sortConfig.direction === "asc" ? (
                                                        <FontAwesomeIcon icon={faArrowUp} style={{ fontSize: "10px" }} />
                                                    ) : sortConfig.direction === "desc" ? (
                                                        <FontAwesomeIcon icon={faArrowDown} style={{ fontSize: "10px" }} />
                                                    ) : (
                                                        // default order icon
                                                        <FontAwesomeIcon icon={faSort} style={{ fontSize: "10px", color: "gray" }} />
                                                    )
                                                ) : (
                                                    // unsorted column
                                                    <FontAwesomeIcon icon={faSort} style={{ fontSize: "12px", }} className="text-gray-400" />
                                                )}
                                            </div>
                                        )}
                                    </div>

                                </th>
                            ))}
                        </tr>

                        {/* Filter Row */}
                        <tr className={`sticky top-[28px] z-10 bg-white mb-1 ${filterClass}`}>
                            <th
                                className={`sticky left-0 bg-gray-50 text-center font-semibold z-10 p-1 border ${filterClass}`}
                                style={{ width: "50px" }}>
                                {expandableRow && filteredData && filteredData.length > 0 ?
                                    <div className="flex items-center gap-1 uppercase bg-white shadow rounded-sm  border-1 border-purple-300"> {/* gap-1 removes extra spacing */}
                                        <Checkbox
                                            checked={expandAllChecked}
                                            onChange={(e) => {
                                                const expand = e.target.checked;
                                                setExpandAllChecked(expand);
                                                expandAll(expand);
                                            }}
                                            color="secondary"
                                            size="small"
                                            sx={{
                                                margin: 0,
                                                padding: 0
                                            }}
                                        />
                                        <span className="text-purple-700">{expandAllChecked ? "Collapse" : "Expand"}</span>
                                    </div> : ""}
                            </th>
                            {columns.map((col) => (
                                <th key={col.accessor} className={` p-1 border-1   text-center ${filterClass}`} style={{ width: col?.width || '100px' }}>
                                    {col.filterable && (
                                        <Autocomplete
                                            multiple
                                            size="small"
                                            options={[
                                                ...new Set(
                                                    data.map((d) => formatValue(d[col.accessor], col.type)).filter(Boolean)
                                                ),
                                            ]}
                                            value={filters[col.accessor] || []}
                                            onChange={(e, newValue) => handleFilterChange(col.accessor, newValue)}
                                            renderTags={() => null}
                                            slotProps={{
                                                chip: {
                                                    size: "small",
                                                    sx: { height: 20, fontSize: "10px", padding: "0 4px", "& .MuiChip-label": { padding: 0 } }
                                                },
                                                input: { sx: { padding: "2px 0px !important", fontSize: "11px" } },
                                                popper: { sx: { fontSize: "12px" } },
                                                listbox: {
                                                    sx: {
                                                        fontSize: "0.6rem",
                                                        "& .MuiAutocomplete-option": { padding: "2px 8px", minHeight: "28px" }
                                                    }
                                                }
                                            }}
                                            sx={{

                                                "& .MuiAutocomplete-inputRoot": { padding: "0px 0px !important", minHeight: "30px", background: "#FFFFFF" },
                                                "& .MuiOutlinedInput-root": { padding: "0px !important", height: "10px" }
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    variant="outlined"
                                                    placeholder="Filter..."
                                                    size="small"
                                                    sx={{
                                                        width: "100%",// col?.width  || "110px",
                                                        // width: ["Pcs"].includes(col.key) ? "10px" : "100px",
                                                        "& .MuiInputLabel-root": { fontSize: "11px" }
                                                    }}
                                                />
                                            )}
                                        />
                                    )}
                                </th>
                            ))}
                        </tr>
                    </thead>


                    {/* {showPaging ? ( */}

                    {/* Normal rendering with pagination */}
                    <tbody className="bg-white">
                        {expandableRow && groupKey ? (
                            groupedData.map((group, gIndex) => (
                                <Fragment key={gIndex}>

                                    {/* ================= GROUP ROW ================= */}
                                    <tr className="bg-slate-50 font-semibold hover:bg-gray-200">
                                        <td className="sticky left-0 bg-salte-50 text-center z-10 p-1 border">
                                            <div className="flex justify-between items-center px-2">
                                                {gIndex + 1}
                                                <button
                                                    onClick={() => toggleRow(gIndex)}
                                                    className="bg-white  text-blue-700"
                                                >
                                                    {expandedRows[gIndex] ? (
                                                        <FontAwesomeIcon icon={faMinus} />
                                                    ) : (
                                                        <FontAwesomeIcon icon={faPlus} />
                                                    )}
                                                </button>
                                            </div>
                                        </td>

                                        {columns.map((col) => (
                                            <td
                                                key={col.accessor}
                                                className="p-1 border bg-slate-50 font-semibold"
                                                align={col.align || "left"}
                                            >
                                                {col.accessor === groupKey
                                                    ? group.groupName
                                                    : col.type === "number"
                                                        ? formatNumber(group.totals[col.accessor])
                                                        : ""}
                                            </td>
                                        ))}
                                    </tr>

                                    {/* ================= DETAIL ROWS ================= */}
                                    {expandedRows[gIndex] && (
                                        <>
                                            {/* DETAIL WRAPPER ROW */}
                                            <tr>
                                                <td
                                                    colSpan={columns.length + 1}
                                                    className="p-2 border"
                                                >
                                                    <h2>Expanded Details </h2>
                                                    <div className=" max-h-[250px] overflow-auto w-full">
                                                        <table className="w-full border-collapse text-xs">
                                                            <thead className="sticky top-0 z-10 bg-slate-200">
                                                                <tr className="bg-gray-100 font-semibold">
                                                                    <th className="border p-1 w-[60px] text-center">
                                                                        #
                                                                    </th>

                                                                    {detailColumns.map((col) => (
                                                                        <th
                                                                            key={col.accessor}
                                                                            className="border p-1 text-center"
                                                                        >
                                                                            {col.header}
                                                                        </th>
                                                                    ))}
                                                                </tr>
                                                            </thead>

                                                            <tbody className="">
                                                                {group.rows.map((row, rIndex) => (
                                                                    <tr
                                                                        key={rIndex}
                                                                        className="hover:bg-yellow-50"
                                                                    >
                                                                        <td className="border p-1 text-center">
                                                                            {`${gIndex + 1}.${rIndex + 1}`}
                                                                        </td>

                                                                        {detailColumns.map((col) => (
                                                                            <td
                                                                                key={col.accessor}
                                                                                className="border p-1"
                                                                                align={col.align || "left"}
                                                                            >
                                                                                {formatValue(
                                                                                    row[col.accessor],
                                                                                    col.type
                                                                                )}
                                                                            </td>
                                                                        ))}
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                            <tfoot className="bg-gray-100 sticky bottom-0 font-semibold">
                                                                <tr>
                                                                    <td className="border p-1 text-center">TOTAL</td>
                                                                    {detailColumns.map((col) => (
                                                                        <td key={col.accessor} className="border p-1 text-right">
                                                                            {col.type === "number"
                                                                                ? formatNumber(
                                                                                    group.rows.reduce(
                                                                                        (sum, r) => sum + (Number(r[col.accessor]) || 0),
                                                                                        0
                                                                                    )
                                                                                )
                                                                                : ""}
                                                                        </td>
                                                                    ))}
                                                                </tr>
                                                            </tfoot>
                                                        </table>
                                                    </div>
                                                </td>
                                            </tr>
                                        </>
                                    )}
                                </Fragment>
                            ))
                        ) : (
                            paginatedData.map((row, idx) => {
                                const rowIndex = (currentPage - 1) * pageSize + idx;
                                return (
                                    <tr key={rowIndex} className="hover:bg-gray-50 border">
                                        <td className="sticky left-0 bg-gray-50 text-center z-10 p-1 border">
                                            {rowIndex + 1}
                                        </td>
                                        {columns.map((col) => (
                                            <td
                                                key={col.accessor}
                                                className="p-1 border"
                                                align={col.align || "left"}
                                            >
                                                {formatValue(row[col.accessor], col.type)}
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })
                        )}
                    </tbody>



                    {/* Totals */}
                    <tfoot className={`bg-slate-200 sticky bottom-0 z-20 font-semibold ${headClass}`}>
                        <tr>
                            <td></td>
                            {columns.map((col, idx) =>
                                col.type === "number" ? (
                                    <td key={col.accessor} className="border p-2 text-right">
                                        {formatNumber(totals[col.accessor])}
                                    </td>
                                ) : idx === 0 ? (
                                    <td key={col.accessor} className="border p-2">
                                        TOTAL
                                    </td>
                                ) : (
                                    <td key={col.accessor} className="border p-2"></td>
                                )
                            )}
                        </tr>
                        {showDiffBal && filteredData && filteredData.length > 0 ?
                            <tr className="bg-red-100 text-red-500 uppercase border-slate-500 ">
                                {columns.map((col, idx) =>
                                    (diff_Balance < 0 && col.accessor === "OpBal_Cr") || (diff_Balance > 0 && col.accessor === "OpBal_Dr") ? (
                                        <td key={col.accessor} className="border p-2 text-right">
                                            {formatNumber(Math.abs(diff_Balance))}
                                        </td>
                                    ) : idx === 0 ? (
                                        <td colSpan={2} key={col.accessor} className="border p-2">
                                            Difference In Opening Balance
                                        </td>
                                    ) : (
                                        <td key={col.accessor} className="border p-2"></td>
                                    )
                                )}
                            </tr> : ""}
                    </tfoot>
                </table>
            </div>
            {/* Pagination Above Grid */}
            {showPaging && filteredData && filteredData.length > 0 && (
                <div className="flex items-center justify-between bg-white border border-gray-200 rounded-md px-4 py-2 shadow-sm mb-3">

                    {/* Left Side - Page Info */}
                    <div className="text-sm text-gray-600 font-medium">
                        Page <span className="text-indigo-600 font-semibold">{currentPage}</span>
                        {" "}of{" "}
                        <span className="text-indigo-600 font-semibold">{totalPages}</span>
                    </div>

                    {/* Right Side - Buttons */}
                    <div className="flex items-center gap-2">

                        {/* Prev Button */}
                        <button
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(prev => prev - 1)}
                            className={`px-3 py-1.5 text-sm rounded-md border transition-all duration-200 
                            ${currentPage === 1
                                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                    : "bg-white text-gray-700 hover:bg-indigo-50 hover:text-indigo-600 border-gray-300"
                                }`}
                        >
                            ← Prev
                        </button>

                        {/* Page Numbers */}
                        {
                            [...Array(totalPages)].map((_, index) => {
                                const page = index + 1;

                                return (
                                    <button
                                        key={page}
                                        onClick={() => setCurrentPage(page)}
                                        className={`w-8 h-8 text-sm rounded-md transition-all duration-200
                                     ${currentPage === page
                                                ? "bg-indigo-600 text-white shadow-md"
                                                : "bg-white border border-gray-300 text-gray-700 hover:bg-indigo-50"
                                            }`}
                                    >
                                        {page}
                                    </button>
                                );
                            })}

                        {/* Next Button */}
                        <button
                            disabled={currentPage === totalPages}
                            onClick={() => setCurrentPage(prev => prev + 1)}
                            className={`px-3 py-1.5 text-sm rounded-md border transition-all duration-200 
                            ${currentPage === totalPages
                                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                    : "bg-white text-gray-700 hover:bg-indigo-50 hover:text-indigo-600 border-gray-300"
                                }`}
                        >
                            Next →
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MasterGrid;